import { useMutation } from '@tanstack/react-query';

export interface UseMarkUserInvitationAsVisitedArgs {
    apiEndpoint: string;
    onSuccess: (isSignedUp: boolean | undefined, variables?: any, context?: any) => void;
    onError: (error: any, variables?: any, context?: any) => void;
}

const mutation = `mutation MarkUserInvitationAsVisited($input: MarkUserInvitationAsVisitedInput!) {
    markUserInvitationAsVisited (input: $input) {
       isSignedUp
      }
  }`;

export const UseMarkUserInvitationAsVisited = (args: UseMarkUserInvitationAsVisitedArgs) => {

    // We are using a hardcoded API token here because this is a public operation and the mutation is enabled only when no user is signed in.
    // The backend authorizer checks the query and if it is the MarkUserInvitationAsVisited mutation, it allows it to pass without checking the token.
    // Note that this is not the recommended way of doing it, as it is not secure.
    // Currently (15.12.2025), we are using it only for one mutation with no sensitive data.
    // If we want to make more queries public, we must consider using a more secure way of authorizing.
    // The recommended way is using an API key for public APIs instead of a user token.
    const apiToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIze31BNyM5RzNbODlHMmIzcD1seCIsIm5hbWUiOiJ1bmF1dGhlbnRpY2F0ZWQgdXNlciBmb3IgcHVibGljIEFQSSIsImlhdCI6MTUxNjIzOTAyMn0.zZZuAyC8PqRmElOqMTyuzpg1KcP953X7vwc-9eeU9_c';

    return useMutation<boolean, Error, string>({
        mutationFn: async (userId: string): Promise<boolean> => {
            // We need fetch here as we need to set the Authorization header and Amplify does not support it.
            const input = { targetUserId: userId };
            const response = await fetch(args.apiEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${apiToken}`,
                },
                body: JSON.stringify({
                    query: mutation,
                    variables: { input },
                    operationName: 'MarkUserInvitationAsVisited',
                }),
            });

            if (!response.ok) throw new Error('Network response was not ok');

            const result = await response.json();

            // We cannot use onError here because we need to navigate to a new component in case of an error.
            if (result?.errors?.length) {
                const queryError = result?.errors[0];
                if (!(queryError.errorType && queryError.message)) {
                    throw new Error();
                }

                throw new Error(queryError.errorType ?? queryError.message);
            }

            return result?.data?.markUserInvitationAsVisited?.isSignedUp ?? undefined;
        },
        onSuccess: (data, _, __) => {
            args.onSuccess(data);
        },
        onError: (data, _, __) => {
            args.onError(data);
        },
    });
};
